import { useState } from 'react';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import { tAccountErrorCode } from 'src/providers/SocketsProvider/Sockets.types';
import { toAccountErrorCode } from './accountErrorCode';

type tWhatsAppWebDisconnectReason = {
  errorCode: tAccountErrorCode | null;
  errorMessage: string | null;
};

const EMPTY_REASON: tWhatsAppWebDisconnectReason = {
  errorCode: null,
  errorMessage: null,
};

type tUseWhatsAppWebDisconnectReasonParams = {
  workspaceId: string | undefined;
};

/**
 * Holds the most recent WhatsApp Web disconnect reason for the open
 * workspace, sourced from the `workspace_event.account_update` socket event.
 *
 * The hook is reactive only — it does NOT seed from REST or storage. A user
 * who opens the app after a disconnection will not see the reason until the
 * next event for that workspace arrives. Reconnect events clear the reason
 * so a stale code does not linger after the session recovers.
 */
export function useWhatsAppWebDisconnectReason(
  params: tUseWhatsAppWebDisconnectReasonParams,
): tWhatsAppWebDisconnectReason {
  const { workspaceId } = params;
  const { workspaceAccountUpdateEvent } = useSocketsContext();

  const [reason, setReason] =
    useState<tWhatsAppWebDisconnectReason>(EMPTY_REASON);

  workspaceAccountUpdateEvent.useSubscription((event) => {
    if (!workspaceId || event.workspace_id !== workspaceId) return;
    if (event.integration_type && event.integration_type !== 'whatsapp_web') {
      return;
    }

    if (event.connected) {
      setReason(EMPTY_REASON);
      return;
    }

    setReason({
      errorCode: toAccountErrorCode(event.error_code),
      errorMessage: event.error_message ?? null,
    });
  });

  return reason;
}
